/**
 * Calendar-day (`YYYY-MM-DD`) parsing, formatting, and arithmetic.
 *
 * The API sends date-only strings already bucketed in the venue's timezone.
 * `new Date('YYYY-MM-DD')` reads them as UTC midnight and `toISOString()`
 * writes the UTC day — both diverge from the local calendar day west of
 * Greenwich, so read and write them as plain calendar days instead.
 */

/**
 * Parses a `YYYY-MM-DD` string as local midnight of that calendar day.
 * @param day - Calendar-day string, e.g. `2026-08-18`
 * @returns Date at 00:00:00 local time of that day
 */
export function parseCalendarDay(day: string): Date {
  return new Date(`${day}T00:00:00`);
}

/**
 * Formats a Date as the `YYYY-MM-DD` calendar day it falls on in local time.
 * @param date - Date to format
 * @returns Calendar-day string, e.g. `2026-08-18`
 */
export function toCalendarDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Adds a number of days to a `YYYY-MM-DD` string, staying in calendar-day
 * space — the result is timezone-independent.
 * @param day - Calendar-day string, e.g. `2026-08-18`
 * @param days - Days to add (negative to subtract)
 * @returns Calendar-day string of the shifted day
 */
export function addCalendarDays(day: string, days: number): string {
  const date = parseCalendarDay(day);
  date.setDate(date.getDate() + days);
  return toCalendarDay(date);
}
